import { useState, useEffect } from 'react';
import { Trophy, Medal } from 'lucide-react';
import axios from 'axios';
import useCompetitionStatus from '../hooks/useCompetitionStatus';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function rankIcon(rank) {
  if (rank === 1) return <Medal className="w-5 h-5 text-[#FFD700]" />;
  if (rank === 2) return <Medal className="w-5 h-5 text-[#C0C0C0]" />;
  if (rank === 3) return <Medal className="w-5 h-5 text-[#CD7F32]" />;
  return <span className="text-sm font-bold text-gray-400">{rank}</span>;
}

function rankRowBg(rank) {
  if (rank === 1) return 'bg-yellow-50 border-yellow-200';
  if (rank === 2) return 'bg-gray-50 border-gray-200';
  if (rank === 3) return 'bg-orange-50 border-orange-200';
  return 'bg-white border-gray-100';
}

export default function FinalLeaderboardCard({ sport, currentUserId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [champion, setChampion] = useState(null);
  const compStatus = useCompetitionStatus();
  const isClosed = compStatus?.closed_sports?.includes(sport);

  useEffect(() => {
    if (!isClosed) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [lb, champs] = await Promise.all([
          axios.get(`${API_URL}/api/leaderboard/overall/${sport}`, { withCredentials: true }),
          axios.get(`${API_URL}/api/champions`, { withCredentials: true }),
        ]);
        if (cancelled) return;
        setRows(lb.data || []);
        const c = (champs.data || []).find(x => x.sport === sport);
        setChampion(c || null);
      } catch (err) {
        // Silent — no card shown if the endpoint fails.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sport, isClosed]);

  if (!isClosed) return null;
  if (loading) return null;
  if (rows.length === 0) return null;

  const sportColor = sport === 'AFL' ? '#E00000' : sport === 'WC' ? '#4338CA' : '#1A1A1A';

  return (
    <div className="tipster-card mb-6" data-testid={`final-leaderboard-${sport.toLowerCase()}`}>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Trophy className="w-5 h-5" style={{ color: sportColor }} />
          <h3 className="text-lg font-bold uppercase tracking-tight text-[#1A1A1A]">
            Final Leaderboard · {sport} 2026
          </h3>
        </div>
        {champion && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Champion</span>
            <span className="font-bold text-[#1A1A1A]">{champion.name}</span>
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-bold uppercase tracking-wider text-white"
              style={{ backgroundColor: sportColor }}
            >
              <Trophy className="w-3 h-3" />
              {champion.points?.toFixed?.(2) ?? champion.points} pts
            </span>
          </div>
        )}
      </div>

      {/* Table Header */}
      <div className="grid grid-cols-[3rem,1fr,4rem,4rem] gap-2 px-3 py-2 text-xs uppercase tracking-wider text-gray-400 font-semibold border-b border-gray-100">
        <div className="text-center">#</div>
        <div>Tipster</div>
        <div className="text-center">Correct</div>
        <div className="text-center">Points</div>
      </div>

      <div className="divide-y divide-gray-50">
        {rows.map((r) => {
          const isMe = r.user_id === currentUserId;
          const pts = ((Math.round((r.total_points || 0) * 100) / 100)).toFixed(2);
          return (
            <div
              key={r.user_id}
              className={`grid grid-cols-[3rem,1fr,4rem,4rem] gap-2 items-center px-3 py-2.5 transition-colors ${rankRowBg(r.rank)} ${isMe ? 'ring-1 ring-[#007AFF]/30 rounded-sm' : ''}`}
              data-testid={`final-lb-row-${r.user_id}`}
            >
              <div className="flex items-center justify-center">{rankIcon(r.rank)}</div>
              <div className="flex items-center gap-2 min-w-0">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                  style={{ background: `linear-gradient(135deg, ${sportColor}, ${sportColor}CC)` }}
                >
                  {r.name.charAt(0)}
                </div>
                <div className="min-w-0">
                  <div className={`font-semibold text-sm truncate ${isMe ? 'text-[#007AFF]' : 'text-[#1A1A1A]'}`}>
                    {r.name}
                    {isMe && <span className="ml-1.5 text-xs text-[#007AFF]">(You)</span>}
                  </div>
                </div>
              </div>
              <div className="text-center text-sm text-gray-600 tabular-nums">
                {r.correct_tips}/{r.total_tips}
              </div>
              <div className="text-center text-base font-black text-[#1A1A1A] tabular-nums">
                {pts}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
